import {Formik, FormikErrors, FormikTouched} from 'formik';

import {FormComponent} from './FormComponent';

import {RecordSchema} from './RecordSchema';
import {toFormikValidationSchema} from 'zod-formik-adapter';

import {FormWithButtons} from '@flexinet/ui-components';

import {Alert, List, Stack} from '@mantine/core';
import {AiOutlineExclamationCircle} from 'react-icons/ai';

import {MeterReading} from '../types';
import {stage} from "../../../config/stage";

const FIELD_LABELS: Record<string, string> = {
    customerId: 'Customer',
    meterId: 'Meter',
    meterValue: 'Meter Value',
    timestamp: 'Timestamp',
    dimensions: 'Dimensions',
};

const flattenErrorMessages = (
    errors: FormikErrors<MeterReading> | Record<string, unknown>,
    path = ''
): { name: string; message: string }[] => {
    const out: { name: string; message: string }[] = [];
    Object.entries(errors).forEach(([key, value]) => {
        const nextPath = path ? `${path}.${key}` : key;
        if (typeof value === 'string') {
            out.push({ name: nextPath, message: value });
        } else if (Array.isArray(value)) {
            value.forEach((item, idx) => {
                if (item && typeof item === 'object') {
                    out.push(...flattenErrorMessages(item as Record<string, unknown>, `${nextPath}[${idx}]`));
                } else if (typeof item === 'string') {
                    out.push({ name: `${nextPath}[${idx}]`, message: item });
                }
            });
        } else if (value && typeof value === 'object') {
            out.push(...flattenErrorMessages(value as Record<string, unknown>, nextPath));
        }
    });
    return out;
};

const formatErrorLabel = (path: string): string => {
    const root = path.split('.')[0].replace(/\[\d+\]$/, '');
    return FIELD_LABELS[root] ?? root;
};

export const RecordForm = ({
                               record,
                               handleSubmit,
                               handleCancel,
                           }: {
    record: Omit<MeterReading, 'id'>,
    handleSubmit: (values: MeterReading) => void,
    handleCancel?: () => void
}) => {

    const onSubmit = (values: MeterReading) => {
        handleSubmit(values);
    };

    const onCancel = () => {
        handleCancel?.();
    };

    let initialValues = {} as MeterReading;

    if (record) {
        initialValues = Object.assign({}, initialValues, record);
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={toFormikValidationSchema(RecordSchema)}
            onSubmit={onSubmit}
        >
            {({
                  handleSubmit,
                  isSubmitting,
                  submitCount,
                  errors,
                  ...rest
              }) => {
                const flattened = flattenErrorMessages(errors as FormikErrors<MeterReading>);
                const showErrorSummary = submitCount > 0 && flattened.length > 0;

                return (
                    <FormWithButtons handleSubmit={handleSubmit} isSubmitting={isSubmitting}
                                     handleCancel={onCancel} debug={false && stage === 'ide'} {...rest as { values: MeterReading; touched: FormikTouched<MeterReading> }}>
                        <Stack gap='md'>
                            {showErrorSummary && (
                                <Alert
                                    data-testid='form-error-summary'
                                    icon={<AiOutlineExclamationCircle/>}
                                    color='red'
                                    variant='light'
                                    title='Please fix the following before saving'
                                >
                                    <List size='sm'>
                                        {flattened.map(({ name, message }) => (
                                            <List.Item key={name}>
                                                <strong>{formatErrorLabel(name)}:</strong> {message}
                                            </List.Item>
                                        ))}
                                    </List>
                                </Alert>
                            )}
                            <FormComponent values={rest.values}/>
                        </Stack>
                    </FormWithButtons>
                );
            }}
        </Formik>
    );
};
